/**
 * BudgetDataEntry — Per-project wrapper around MetricEntryGrid for Finance
 * actual-spend data entry.
 *
 * Mirrors ScheduleDataEntry one-for-one; the only differences are which API
 * surface gets called (`FinanceSeriesPoints`) and which value lands in the
 * row (actualSpend instead of actualComplete). See ScheduleDataEntry for
 * rationale on the batch-upsert pattern, cache invalidation, and inputs.
 *
 * Inputs:
 *   - projectGuid                Project UUID for the API write.
 *   - financeInstances           Finance SubKpiInstance[] from kpiConfigs.
 *   - latestPointsByInstance     Map of instance_guid (UPPER) → latest
 *                                BudgetSeriesPointResponse.
 *   - canEdit                    Permission check.
 *   - onDataSaved                Refresh callback.
 *
 * Save handler dispatches CACHE_UPDATE_EVENT with QUERY_KEYS.finance so
 * `useApiFinanceData` re-fetches before the Data Reliability engine
 * re-classifies the affected instance.
 */

import React, { useCallback, useMemo } from 'react';
import MetricEntryGrid, {
  type MetricRowDef,
  type MetricEntrySavePayload,
} from './MetricEntryGrid';
import MetricEntryStacked from './MetricEntryStacked';
import {
  batchUpsertBudgetSeriesPoints,
  type BudgetSeriesPointResponse,
} from '../../api/budgetSeriesPoints';
import type { SubKpiInstance } from '../../Data/ProjectConfig/projectKpiConfigOverrides';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { invalidateAndRefetch, QUERY_KEYS } from '../../api/queryCache';

// ── Types ───────────────────────────────────────────────────

export interface BudgetDataEntryProps {
  /** Project GUID for the API write. */
  projectGuid: string;
  /** Finance sub-KPI instances configured on this project. */
  financeInstances: SubKpiInstance[];
  /**
   * Latest finance series point per instance, keyed by instance_guid
   * (upper-cased). Populates the Current column. Missing entries render
   * as `—`.
   */
  latestPointsByInstance?: Record<string, BudgetSeriesPointResponse>;
  /** Whether the current user has edit permission. */
  canEdit: boolean;
  /** Called after a successful save so the host can refresh data. Receives
   *  save coverage so the host can tell a FULL save (every metric in the grid
   *  filled this save) from a PARTIAL one — the Action Center only resolves
   *  the data item on full coverage (DEV-1256). */
  onDataSaved?: (info?: { savedCount: number; totalCount: number }) => void;
  /** Optional title override; defaults to translated "Enter Actual Spend". */
  title?: string;
  /** Layout variant: "grid" (wide, default) or "stacked" (narrow tray). */
  variant?: 'grid' | 'stacked';
}

// ── Component ───────────────────────────────────────────────

const BudgetDataEntry: React.FC<BudgetDataEntryProps> = ({
  projectGuid,
  financeInstances,
  latestPointsByInstance,
  canEdit,
  onDataSaved,
  title,
  variant = 'grid',
}) => {
  const { t } = useAppTranslation('kpi');

  const metrics: MetricRowDef[] = useMemo(() => {
    if (!Array.isArray(financeInstances) || financeInstances.length === 0) {
      return [];
    }
    return financeInstances.map((inst) => {
      const guid = String(inst?.instance_guid || '').toUpperCase();
      const latest = latestPointsByInstance?.[guid];
      return {
        key: guid,
        label: String(inst?.display_name || guid),
        // Finance unit is currency-dependent and varies per project; leave
        // unset so MetricEntryGrid renders without a unit suffix. A future
        // pass can read project.currency_code and pass it through.
        unit: undefined,
        currentValue: latest?.actualSpend ?? null,
        // Finance "target" is the baseline budget — the planned spend for the
        // period. Showing it gives the user a frame of reference. May be null
        // for instances without a baseline yet; the grid handles null fine.
        targetValue: latest?.baselineBudget ?? null,
        // Higher actual_spend isn't intrinsically "good" — it just means money
        // got spent. We keep direction unset rather than guessing.
        direction: undefined,
        valueType: 'numeric' as const,
      };
    });
  }, [financeInstances, latestPointsByInstance]);

  const handleSave = useCallback(
    async (entries: MetricEntrySavePayload[]) => {
      if (entries.length === 0) return;
      await batchUpsertBudgetSeriesPoints(
        entries.map((entry) => ({
          projectId: projectGuid,
          instanceGuid: entry.key,
          reportingDate: entry.reportingDate,
          actualSpend: entry.value,
        })),
      );

      // invalidateAndRefetch (not raw dispatchEvent) — see queryCache.ts
      // comment about why subscribers don't refetch on a bare event.
      invalidateAndRefetch(QUERY_KEYS.finance);

      if (onDataSaved) onDataSaved({ savedCount: entries.length, totalCount: metrics.length });
    },
    [projectGuid, onDataSaved, metrics.length],
  );

  if (metrics.length === 0 || !canEdit) return null;

  const Comp = variant === 'stacked' ? MetricEntryStacked : MetricEntryGrid;
  return (
    <Comp
      metrics={metrics}
      onSave={handleSave}
      readOnly={!canEdit}
      title={title || t('dataEntry.budgetTitle') || 'Enter Actual Spend'}
    />
  );
};

export default BudgetDataEntry;
